import { cryptoService } from "../authentication/CryptoService";
import { UserId } from "../authentication/UserId";
import { JsonWebKeyPair, Settings } from "../contexts/AuthenticationContext";
import { PublicProfile } from "../profile/PublicProfile";
import {
  ContactEntry,
  GroupEntry,
  SharedFolderEntry,
} from "../document/DocumentMetadata";
import { ContactInfo, ContactRequest } from "./ContactRequest";
import { contactRepository, ContactRepository } from "./ContactRepository";
import { parseOrigin } from "../api/RemoteApiClient";
import { repositoriesFor } from "../api/repositoriesFor";
import { federationRepository } from "../federation/FederationRepository";
import { chatPlace } from "../federation/whereabouts";
import { servicesFor } from "../api/servicesFor";
import {
  documentRepository,
  HttpError,
  PreconditionFailedError,
} from "../document/DocumentRepository";
import {
  DocumentLoadError,
  documentService,
} from "../document/DocumentService";

// How often updateContact re-reads the "chats" document and
// retries after the server rejected the write because it changed concurrently
// (e.g. the same chat open in two tabs, or a contact-request accept/receive
// racing this update) - same reasoning as DocumentService.storeDocument's own
// retry loop.
const MAX_CHATS_UPDATE_RETRIES = 3;

// The "chats" document's list content - both `contacts` and `groups` need to
// survive every read-modify-write of this document (see updateChatList
// below), not just whichever one a particular caller is patching.
export type ChatList = {
  contacts: ContactEntry[];
  groups: GroupEntry[];
  // Undefined = not known by the caller; updateChatList then looks it up
  // instead of dropping the entries on write.
  sharedFolders?: SharedFolderEntry[];
};

// Re-fetches and decrypts the "chats" document with a key we already hold,
// returning just what updateChatList needs to re-apply its change after a
// concurrent-modification 412: the current list and the current revision.
// Exported for GroupService.createGroup's own atomic-upload retry (it can't
// go through updateChatList/updateDocumentMetadata since the group Document
// and the "chats" list are created together in one multipart upload).
export async function reloadChatList(
  userId: UserId,
  chatsDocumentId: string,
  chatsDocumentKey: JsonWebKey,
): Promise<{ list: ChatList; revision: string | null }> {
  const { content, etag } = await documentRepository.loadDocument(
    userId,
    chatsDocumentId,
  );
  const decrypted = await cryptoService.decryptDocument(
    chatsDocumentKey,
    content,
  );
  const payload = JSON.parse(new TextDecoder().decode(decrypted));
  return {
    list: {
      contacts: payload.contacts ?? [],
      groups: payload.groups ?? [],
      sharedFolders: payload.sharedFolders ?? [],
    },
    revision: etag,
  };
}

// Appends a contact to the "chats" document's list, replacing any existing
// entry for the same chat (chatId is a freshly generated uuid, unique per
// chat/contact). Both handshake steps are non-atomic read-modify-writes
// followed by a separate server call (accept: the repository call; receive:
// confirmContactRequestReceived) - if that trailing call fails the request
// stays pending and the whole step is retried on the next poll, by which
// point our first attempt's list write has already landed. Deduping here
// keeps the retry from growing the list without bound.
function appendContact(
  existing: ContactEntry[],
  contact: ContactEntry,
): ContactEntry[] {
  const others = existing.filter((c) => c.chatId !== contact.chatId);
  return [...others, contact];
}

// Until the inviter has created the chat Document and the server has filed the
// invitee's key entry (ADR 0015 leg 3), loading it answers 401/403 (no key
// entry yet) or 404. Anything else - a server error, a network failure, an
// undecryptable key - is a real error and must not be masked by `pending`.
function isChatNotAccessibleYet(e: unknown): boolean {
  return (
    e instanceof DocumentLoadError &&
    e.cause instanceof HttpError &&
    [401, 403, 404].includes(e.cause.status)
  );
}

// Both parties' "public-profile" Document ids, keyed by UserId. The other
// party's id is only missing if their public-profile somehow does not exist
// yet (should not normally happen, see docs/plans/chat-public-profile.md §3.6).
function makePublicProfiles(
  ownUserId: string,
  ownPublicProfileId: string,
  contactUserId: string,
  contactPublicProfileId: string | undefined,
): Record<string, string> {
  return {
    [ownUserId]: ownPublicProfileId,
    ...(contactPublicProfileId
      ? { [contactUserId]: contactPublicProfileId }
      : {}),
  };
}

// The other party's public profile isn't reachable yet at accept/receive
// time - name/email come from what they told on the contact request (see
// ContactRequest.contactInfo), if anything. profileRevision starts as "" so
// the first time this chat is opened, the mismatch against the real loaded
// PublicProfileMetadata.revision triggers a snapshot refresh (see Chat.tsx /
// updateContactProfileSnapshot).
function makePlaceholderContactEntry(
  userId: string,
  chatId: string,
  owner: string,
  info: ContactInfo,
): ContactEntry {
  return {
    userId,
    chatId,
    owner,
    name: info.name ?? "",
    ...(info.email ? { email: info.email } : {}),
    profileRevision: "",
    // Where the contact's profile lives, if they accepted with an account on another server.
    ...(info.home ? { profileHome: info.home } : {}),
  };
}

// The `home` of a ContactInfo comes from the other party: only a well-formed origin and id pass.
function parseHome(home: unknown): ContactInfo["home"] {
  const { origin, userId } = (home ?? {}) as Record<string, unknown>;
  if (typeof origin !== "string" || typeof userId !== "string" || !userId) {
    return undefined;
  }
  try {
    return { origin: parseOrigin(origin), userId };
  } catch {
    return undefined;
  }
}

async function encryptContactInfo(
  info: ContactInfo,
  key: JsonWebKey,
): Promise<string> {
  return cryptoService.encryptMessage(JSON.stringify(info), key);
}

// Never rejects: contact info is a display nicety only - a missing or
// undecryptable one (e.g. the invitee signed in with a different address than
// the one they were invited with) resolves to {}.
async function decryptContactInfo(
  encrypted: string | undefined,
  key: JsonWebKey,
): Promise<ContactInfo> {
  if (!encrypted) {
    return {};
  }
  try {
    const info = JSON.parse(await cryptoService.decryptMessage(encrypted, key));
    return {
      name:
        typeof info.name === "string"
          ? info.name.trim() || undefined
          : undefined,
      email:
        typeof info.email === "string" ? info.email || undefined : undefined,
      home: parseHome(info.home),
    };
  } catch (e) {
    console.error("Failed to decrypt contact info", e);
    return {};
  }
}

// Loads the "chats" document with the user's settings key - the one place that knows its shape
// (the lists a document written before they existed lacks are empty).
export async function loadChatsDocument(
  userId: UserId,
  chatsId: string,
  settingsKey: JsonWebKey,
): Promise<ChatsDocumentState & { revision: string }> {
  const loaded = await documentService.loadDocument(
    userId,
    chatsId,
    userId,
    settingsKey,
  );
  if (loaded.type !== "chatList") {
    throw new Error(`Expected a chatList, got ${loaded.type}`);
  }
  return {
    documentId: loaded.documentId,
    name: loaded.name,
    key: loaded.key,
    revision: loaded.revision,
    contacts: loaded.contacts,
    groups: loaded.groups ?? [],
    sharedFolders: loaded.sharedFolders ?? [],
  };
}

// What the chat view keeps of the "chats" document to write it back.
export type ChatsDocumentState = {
  documentId: string;
  name: string;
  key: JsonWebKey;
  revision: string | null;
} & ChatList;

// The shared folders of the "chats" document. A caller that did not read them
// (most of the chat views only keep contacts and groups) cannot write them
// back, so they are looked up: a write built on a list without them would
// silently drop every shared folder. A concurrent change between this read and
// the write is caught by the revision check of the write itself.
export async function knownSharedFolders(
  userId: UserId,
  chatsDocument: Pick<ChatsDocumentState, "documentId" | "key"> & {
    sharedFolders?: SharedFolderEntry[];
  },
): Promise<SharedFolderEntry[]> {
  if (chatsDocument.sharedFolders) {
    return chatsDocument.sharedFolders;
  }
  const { list } = await reloadChatList(
    userId,
    chatsDocument.documentId,
    chatsDocument.key,
  );
  return list.sharedFolders ?? [];
}

// Applies `patch` to the "chats" document's whole list (contacts and groups)
// and writes it back - a read-modify-write that re-reads the document and
// re-applies the patch if the server rejects the write because it changed
// concurrently (e.g. the same chat open in two tabs, a contact-request
// accept/receive, a group being created/joined, or the chat view's own
// profile-snapshot and pending-key updates racing each other) - same
// reasoning as DocumentService.storeDocument's own retry loop. Generalized
// over the whole list (not just `contacts`) so a write from either side
// (contacts or groups) never drops the other's entries.
export async function updateChatList(
  userId: UserId,
  chatsDocument: Pick<
    ChatsDocumentState,
    "documentId" | "name" | "key" | "revision"
  > &
    ChatList,
  patch: (list: ChatList) => ChatList,
): Promise<{ list: ChatList; revision: string | null }> {
  let currentList: ChatList = {
    contacts: chatsDocument.contacts,
    groups: chatsDocument.groups,
    sharedFolders: await knownSharedFolders(userId, chatsDocument),
  };
  let currentRevision = chatsDocument.revision;
  for (let attempt = 1; attempt <= MAX_CHATS_UPDATE_RETRIES; attempt++) {
    const updatedList = patch(currentList);
    try {
      const newRevision = await documentService.updateDocumentMetadata(
        userId,
        chatsDocument.documentId,
        chatsDocument.key,
        {
          name: chatsDocument.name,
          type: "chatList",
          contacts: updatedList.contacts,
          groups: updatedList.groups,
          sharedFolders: updatedList.sharedFolders,
        },
        currentRevision,
      );
      return { list: updatedList, revision: newRevision };
    } catch (e) {
      if (
        !(e instanceof PreconditionFailedError) ||
        attempt >= MAX_CHATS_UPDATE_RETRIES
      ) {
        throw e;
      }
      const reloaded = await reloadChatList(
        userId,
        chatsDocument.documentId,
        chatsDocument.key,
      );
      currentList = reloaded.list;
      currentRevision = reloaded.revision;
    }
  }
  // Unreachable: the final iteration either returns or rethrows.
  throw new PreconditionFailedError("Chats document update retries exhausted");
}

// Applies `patch` to one contact of the "chats" document, leaving `groups`
// untouched - a thin wrapper around updateChatList for the contact-only
// callers below.
async function updateContact(
  userId: UserId,
  chatsDocument: ChatsDocumentState,
  contactUserId: string,
  patch: (contact: ContactEntry) => ContactEntry,
): Promise<{ contacts: ContactEntry[]; revision: string | null }> {
  const { list, revision } = await updateChatList(
    userId,
    chatsDocument,
    (current) => ({
      ...current,
      contacts: current.contacts.map((contact) =>
        contact.userId === contactUserId ? patch(contact) : contact,
      ),
    }),
  );
  return { contacts: list.contacts, revision };
}

// Accepting an invitation with an account on another server (ADR 0013 A11): the exchange lives
// on the inviter's server, while keys, chat list and the shared profile stay on the own one.
export interface AcceptContext {
  // Where the exchange lives (the inviter's server) - the own repository for a local accept.
  contacts: ContactRepository;
  // Own UserId THERE. The inviter derives the chat key with it as `invitee`, so must we.
  selfId: UserId;
  // Whom to share the own public profile with HERE (the inviter's id on the own server), if
  // known; without one the profile is not shared.
  profileGrantee?: UserId;
  // What the invitation says about the inviter, if the caller has read it already.
  inviterInfo?: ContactInfo;
  // The inviter's server, if it is not the own one.
  chatOrigin?: string;
  // The address the invitation was redeemed with.
  selfAddress?: string;
  // Where the own profile lives: the own server and the own UserId there.
  home?: { origin: string; userId: string };
}

export const contactService = {
  // Inviter side, leg 1: encrypts our own name/address for the invitee, who
  // can read it before accepting (see ContactRequest.contactInfo).
  encryptInvitationInfo: async (
    info: ContactInfo,
    inviteeEmail: string,
    chatId: string,
  ): Promise<string> =>
    encryptContactInfo(
      info,
      await cryptoService.deriveInvitationKey(inviteeEmail, chatId),
    ),

  // Invitee side: reads the inviter's name/address off an INVITED request.
  // Resolves to {} if it cannot be read.
  readInvitationInfo: async (
    request: Pick<ContactRequest, "chatId" | "contactInfo">,
    ownEmail: string | undefined,
  ): Promise<ContactInfo> => {
    if (!ownEmail) {
      return {};
    }
    return decryptContactInfo(
      request.contactInfo,
      await cryptoService.deriveInvitationKey(ownEmail, request.chatId),
    );
  },

  // Invitee side, leg 2 of the handshake (ADR 0015): accept an INVITED
  // request. The inviter owns the chat and creates its Document later (leg 3);
  // we only derive the chat key (ECDH + HKDF, nothing is transported), record
  // the contact - with the key in its `pending` part, so the chat is usable
  // right away - share our public profile into the chat, and hand the server
  // our own entry for the chat key (wrapped under our "chats" document key),
  // which it files under the chat Document once the inviter created it.
  acceptContactRequest: async (
    userId: UserId,
    invitation: Pick<
      ContactRequest,
      "inviter" | "chatId" | "publicKey" | "publicProfileId" | "contactInfo"
    >,
    ownEmail: string | undefined,
    ownPublicProfile: PublicProfile,
    settings: Settings,
    mainKeyPair: JsonWebKeyPair,
    context: AcceptContext = {
      contacts: contactRepository,
      selfId: userId,
      profileGrantee: invitation.inviter,
    },
  ): Promise<ContactEntry> => {
    const {
      inviter: contactId,
      chatId,
      publicKey: inviterPublicKey,
      publicProfileId: inviterPublicProfileId,
    } = invitation;
    const { selfId } = context;
    const selfAddress = context.selfAddress ?? ownEmail;
    try {
      const chatsDocument = await documentService.loadDocument(
        userId,
        settings.chats,
        userId,
        settings.settingsKey,
      );
      if (chatsDocument.type !== "chatList") {
        throw new Error(
          `Expected the "chats" document to be a chatList, got ${chatsDocument.type}`,
        );
      }

      const chatKey = await cryptoService.deriveChatKey(
        mainKeyPair.privateKey,
        inviterPublicKey,
        chatId,
        contactId,
        selfId,
      );
      const wrappedChatKey = await cryptoService.encryptKey(
        chatKey,
        chatsDocument.key,
      );
      // Both parties' "public-profile" Document ids (see
      // docs/plans/chat-public-profile.md §3.3) - the inviter writes them into
      // the chat's metadata in leg 3; until then we keep them in `pending`.
      const publicProfiles = makePublicProfiles(
        selfId,
        ownPublicProfile.documentId,
        contactId,
        inviterPublicProfileId,
      );

      const contact: ContactEntry = {
        ...makePlaceholderContactEntry(
          contactId,
          chatId,
          contactId,
          context.inviterInfo ??
            (await contactService.readInvitationInfo(invitation, selfAddress)),
        ),
        pending: { chatKey, publicProfiles },
        ...(context.chatOrigin && selfAddress
          ? {
              chatOrigin: context.chatOrigin,
              self: { userId: selfId, address: selfAddress },
              ...(context.profileGrantee
                ? { localUserId: context.profileGrantee }
                : {}),
            }
          : {}),
      };
      await documentService.updateDocumentMetadata(
        userId,
        settings.chats,
        chatsDocument.key,
        {
          name: chatsDocument.name,
          type: chatsDocument.type,
          contacts: appendContact(chatsDocument.contacts, contact),
          groups: chatsDocument.groups,
          sharedFolders: await knownSharedFolders(userId, {
            ...chatsDocument,
            documentId: settings.chats,
          }),
        },
        // Reject (rather than silently clobber) if the "chats" document changed
        // since we loaded it - another accepted request would otherwise be lost.
        chatsDocument.revision,
      );

      // Share our own public profile into the chat (§3.2): a keys/{contactId}.json entry under our
      // ppId, wrapped with the chat key - the same mechanism documentService.shareDocument uses for
      // any other document shared into a chat. Done before the accept so a failure leaves the
      // request INVITED and the whole step can be retried.
      if (context.profileGrantee) {
        await documentService.shareDocument(
          userId,
          {
            documentId: ownPublicProfile.documentId,
            key: ownPublicProfile.key,
          },
          context.profileGrantee,
          chatKey,
        );
      }

      await context.contacts.acceptContactRequest(
        selfId,
        contactId,
        mainKeyPair.publicKey,
        wrappedChatKey,
        ownPublicProfile.documentId,
        // Our own name/address for the inviter, who can read it with the
        // chat key they derive in leg 3.
        await encryptContactInfo(
          {
            name: ownPublicProfile.name,
            email: selfAddress,
            ...(context.home ? { home: context.home } : {}),
          },
          chatKey,
        ),
      );

      return contact;
    } catch (e) {
      console.error(
        "Error in acceptContactRequest",
        typeof e,
        e,
        e instanceof Error ? e.stack : "",
      );
      throw e;
    }
  },

  // Invitee side, leg 2 for an invitation of ANOTHER server (ADR 0013 A11): `invitation` was
  // read at `from` with the guest session (`selfId` is the invitee's UserId there). Records the
  // inviter as a foreign principal on the own server - under the address from their contactInfo,
  // without which the profile is not shared, the accept itself still works -, accepts at `from`
  // and remembers the address the guest session renews with.
  acceptForeignInvitation: async (
    userId: UserId,
    from: string,
    selfId: UserId,
    selfAddress: string,
    inviter: ContactInfo,
    invitation: Pick<
      ContactRequest,
      "inviter" | "chatId" | "publicKey" | "publicProfileId" | "contactInfo"
    >,
    ownPublicProfile: PublicProfile,
    settings: Settings,
    mainKeyPair: JsonWebKeyPair,
  ): Promise<ContactEntry> => {
    const origin = parseOrigin(from);
    const profileGrantee = inviter.email
      ? await federationRepository.registerForeignPrincipal(
          userId,
          new URL(origin).host,
          inviter.email,
        )
      : undefined;
    const contact = await contactService.acceptContactRequest(
      userId,
      invitation,
      selfAddress,
      ownPublicProfile,
      settings,
      mainKeyPair,
      {
        contacts: repositoriesFor(origin).contacts,
        selfId,
        profileGrantee,
        chatOrigin: origin,
        selfAddress,
        inviterInfo: inviter,
        home: { origin: window.location.origin, userId },
      },
    );
    return contact;
  },

  // Inviter side, leg 3 of the handshake (ADR 0015): pick up a request the
  // invitee has ACCEPTED. Derives the chat key from the invitee's public key,
  // creates the chat Document under our own "chats" document (atomically
  // together with the new contact entry), shares our public profile into the
  // chat and confirms receipt - whereupon the server files the invitee's key
  // entry under the chat Document.
  receiveContactRequest: async (
    userId: UserId,
    request: ContactRequest,
    ownPublicProfile: PublicProfile,
    settings: Settings,
    mainKeyPair: JsonWebKeyPair,
  ): Promise<ContactEntry> => {
    const chatId = request.chatId;

    const chatsDocument = await documentService.loadDocument(
      userId,
      settings.chats,
      userId,
      settings.settingsKey,
    );
    if (chatsDocument.type !== "chatList") {
      throw new Error(
        `Expected the "chats" document to be a chatList, got ${chatsDocument.type}`,
      );
    }

    const chatKey = await cryptoService.deriveChatKey(
      mainKeyPair.privateKey,
      request.publicKey,
      chatId,
      userId,
      request.invitee,
    );

    // A retry after a failed confirm finds the chat already created (the
    // contact entry and the chat Document are written in one atomic upload).
    const existing = chatsDocument.contacts.find((c) => c.chatId === chatId);
    const contact =
      existing ??
      makePlaceholderContactEntry(
        request.invitee,
        chatId,
        userId,
        await decryptContactInfo(request.contactInfo, chatKey),
      );
    if (!existing) {
      const [encryptedChatContent] = await cryptoService.encryptDocument(
        chatKey,
        [
          new TextEncoder().encode(
            JSON.stringify({
              documentId: chatId,
              name: request.invitee,
              type: "chat",
              publicProfiles: makePublicProfiles(
                userId,
                ownPublicProfile.documentId,
                request.invitee,
                request.publicProfileId,
              ),
            }),
          ).buffer,
        ],
      );
      const [encryptedChatsContent] = await cryptoService.encryptDocument(
        chatsDocument.key,
        [
          new TextEncoder().encode(
            JSON.stringify({
              name: chatsDocument.name,
              type: chatsDocument.type,
              contacts: appendContact(chatsDocument.contacts, contact),
              groups: chatsDocument.groups,
              sharedFolders: await knownSharedFolders(userId, {
                ...chatsDocument,
                documentId: settings.chats,
              }),
            }),
          ).buffer,
        ],
      );
      await documentRepository.uploadDocument(
        userId,
        userId, // the chat is created under the inviter's own "chats" document
        settings.chats,
        encryptedChatsContent,
        chatsDocument.revision,
        chatId,
        encryptedChatContent,
        {
          issuer: userId,
          kid: settings.chats,
          sharedKey: await cryptoService.encryptKey(chatKey, chatsDocument.key),
        },
        [],
      );
    }

    // Share our own public profile into the chat (§3.2/§4): only we can grant the invitee read
    // access to ours (issuer = them, filed under our own ppId).
    await documentService.shareDocument(
      userId,
      { documentId: ownPublicProfile.documentId, key: ownPublicProfile.key },
      request.invitee,
      chatKey,
    );

    await contactRepository.confirmContactRequestReceived(
      userId,
      request.invitee,
    );

    return contact;
  },

  // Loads the symmetric key of a chat's Document. Both parties keep their
  // own key entry wrapped symmetrically under their own "chats" document's
  // key: the owner (the inviter) filed theirs when creating the chat
  // Document; the invitee's entry was filed by the server during the
  // receipt-confirmation step. Either way it unwraps with our own
  // chats-document key. Until the inviter has created the chat Document, the
  // invitee falls back to the `pending` part of their contact entry.
  loadChatKey: async (
    user: UserId,
    contact: ContactEntry,
    chatsId: string,
    chatsDocumentKey: JsonWebKey,
  ): Promise<{
    key: JsonWebKey;
    publicProfiles: Record<string, string>;
    // true if the chat Document could not be loaded and `contact.pending` was
    // used instead.
    pending: boolean;
  }> => {
    let document;
    try {
      // On another server we are a guest there: the key entry hangs under our id on that server.
      const place = chatPlace(contact, user);
      document =
        contact.owner === user
          ? await documentService.loadDocument(
              user,
              contact.chatId,
              chatsId,
              chatsDocumentKey,
            )
          : await servicesFor(place.origin).documents.loadDocument(
              contact.owner,
              contact.chatId,
              place.selfId,
              chatsDocumentKey,
            );
    } catch (e) {
      if (contact.pending && isChatNotAccessibleYet(e)) {
        return {
          key: contact.pending.chatKey,
          publicProfiles: contact.pending.publicProfiles,
          pending: true,
        };
      }
      throw e;
    }
    if (document.type !== "chat") {
      throw new Error(`Expected a chat document, got ${document.type}`);
    }
    return {
      key: document.key,
      publicProfiles: document.publicProfiles,
      pending: false,
    };
  },

  // The id of `contact` on THIS server (F9: the member id of a group on this server), or
  // undefined if there is no address to look it up with. Registering is idempotent, so nothing
  // is stored; resolveShareGrantee stores the result.
  localIdOf: async (
    userId: UserId,
    contact: ContactEntry,
  ): Promise<UserId | undefined> => {
    if (!contact.chatOrigin) {
      return contact.userId;
    }
    if (contact.localUserId) {
      return contact.localUserId;
    }
    if (!contact.email) {
      return undefined;
    }
    return federationRepository.registerForeignPrincipal(
      userId,
      new URL(contact.chatOrigin).host,
      contact.email,
    );
  },

  // The grantee of a document we share into the chat with `contact` - their id ON OUR SERVER,
  // where the document stays (F8). A contact of this server is that id itself. For one whose chat
  // lives elsewhere it is their ForeignUserMapping here; entries from before F8 do not carry it
  // yet, so it is looked up (localIdOf) and stored. Resolves to undefined if there is no address
  // to register (ADR 0013 decision 10) - then nothing can be shared.
  resolveShareGrantee: async (
    userId: UserId,
    chatsDocument: ChatsDocumentState,
    contact: ContactEntry,
  ): Promise<
    | { grantee: UserId; contacts?: ContactEntry[]; revision?: string | null }
    | undefined
  > => {
    const grantee = await contactService.localIdOf(userId, contact);
    if (!grantee) {
      return undefined;
    }
    if (!contact.chatOrigin || contact.localUserId) {
      return { grantee };
    }
    const { contacts, revision } = await updateContact(
      userId,
      chatsDocument,
      contact.userId,
      (current) => ({ ...current, localUserId: grantee }),
    );
    return { grantee, contacts, revision };
  },

  // Patches one contact's denormalized public-profile snapshot (name/avatarId
  // /profileRevision) in the "chats" document - called from the chat view
  // when the freshly-loaded PublicProfileMetadata.revision no longer matches
  // the cached ContactEntry.profileRevision.
  updateContactProfileSnapshot: (
    userId: UserId,
    chatsDocument: ChatsDocumentState,
    contactUserId: string,
    snapshot: { name: string; avatarId?: string; revision: string },
  ): Promise<{ contacts: ContactEntry[]; revision: string | null }> =>
    updateContact(userId, chatsDocument, contactUserId, (contact) => ({
      ...contact,
      name: snapshot.name,
      avatarId: snapshot.avatarId,
      profileRevision: snapshot.revision,
    })),

  // Removes a contact's `pending` chat key (ADR 0015 decision 5) - called
  // from the chat view once the chat Document itself has been loaded, i.e.
  // the inviter has created it and the server has filed our key entry.
  dropPendingChatKey: (
    userId: UserId,
    chatsDocument: ChatsDocumentState,
    contactUserId: string,
  ): Promise<{ contacts: ContactEntry[]; revision: string | null }> =>
    updateContact(userId, chatsDocument, contactUserId, (contact) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { pending, ...rest } = contact;
      return rest;
    }),
};
